import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import Dashboard from "./Dashboard";
import MonthPicker from "./ui/MonthPicker";
import DailyEntry from "./DailyEntry";
import DeficitSection from "./DeficitSection";
import Reports from "./Reports";
import AttendanceReport from "./AttendanceReport";
import ReviewersMgmt from "./ReviewersMgmt";
import StationsMgmt from "./StationsMgmt";
import AssignmentsMgmt from "./AssignmentsMgmt";
import Salaries from "./Salaries";
import UsersMgmt from "./UsersMgmt";
import ImportExcel from "./ImportExcel";

const TABS = [
  { id: "dashboard", label: "لوحة التحكم", icon: "📊" },
  { id: "daily", label: "الإدخال اليومي", icon: "📝" },
  { id: "deficit", label: "عجز الردود", icon: "⚠️" },
  { id: "reports", label: "التقارير", icon: "📋" },
  { id: "attendance_report", label: "تقرير الدوام", icon: "🕐" },
  { id: "reviewers", label: "المراجعون", icon: "👥" },
  { id: "stations", label: "المحطات", icon: "🏭" },
  { id: "assignments", label: "التعيينات", icon: "🔗" },
  { id: "salaries", label: "الرواتب", icon: "💰" },
  { id: "users", label: "المستخدمون", icon: "🔐" },
  { id: "import", label: "استيراد", icon: "📥" },
];

// Accounts created purely to view & export reports get a stripped-down shell:
// only the dashboard and reports tabs, nothing that lets them see/change data.
const VIEWER_ALLOWED_TABS = ["dashboard", "reports"];

export default function SupervisorApp({ user, toast, onLogout }) {
  const [tab, setTab] = useState("dashboard");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [reviewers, setReviewers] = useState([]);
  const [stations, setStations] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [rv, st, as] = await Promise.all([
        api("/reviewers"),
        api("/stations"),
        api("/assignments"),
      ]);
      setReviewers(rv || []);
      setStations(st || []);
      setAssignments(as || []);
    } catch (e) {
      toast("خطأ في تحميل البيانات", "error");
    }
  }, [toast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Every screen except reviewer management should only offer *active*
  // reviewers when picking who to assign work to, log entries for, pay,
  // etc. — a reviewer whose account was stopped shouldn't keep showing up
  // as a selectable option system-wide. Reviewer management itself still
  // gets the full list (via an explicit prop override below) so it can
  // display and reactivate stopped reviewers.
  const activeReviewers = reviewers.filter((r) => r.active);

  const props = {
    reviewers: activeReviewers,
    stations,
    assignments,
    toast,
    refresh: loadAll,
    month,
    setMonth,
    user,
  };

  return (
    <div
      dir="rtl"
      style={{
        minHeight: "100vh",
        background: "#020817",
        fontFamily: "'Cairo','Tajawal',sans-serif",
      }}
    >
      {/* Header */}
      <style>{`
        /* On narrow/mobile screens the header's fixed-width pieces (logo,
           month picker, user badge, logout button) were squeezing the tabs
           strip down to almost nothing, so tabs past the first one (e.g.
           "التقارير" for a viewer account) were scrolled off-screen with no
           visible hint that more tabs existed. Force the tabs onto their
           own full-width row instead of fighting for space. */
        @media (max-width: 720px) {
          .sv-header-row { flex-wrap: wrap; height: auto !important; padding: 8px 12px !important; row-gap: 8px; }
          .sv-header-tabs { order: 2; flex: 1 1 100% !important; }
          .sv-header-right { order: 3; flex: 1 1 100% !important; justify-content: space-between !important; }
          .sv-header-divider { display: none !important; }
        }
      `}</style>
      <div
        style={{
          background: "linear-gradient(135deg,#0f172a,#0f2744)",
          borderBottom: "1px solid #1e3a5f",
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 4px 24px #000a",
        }}
      >
        <div
          className="sv-header-row"
          style={{
            maxWidth: 1500,
            margin: "0 auto",
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: 60,
          }}
        >
          {/* Logo */}
          <a href="/" onClick={(e) => { e.preventDefault(); setTab("dashboard"); }}
            style={{
              display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
              textDecoration: "none",
            }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "linear-gradient(135deg,#38bdf8,#818cf8)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18, boxShadow: "0 2px 12px #38bdf844",
            }}>
              📷
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 800, lineHeight: 1.3 }}>
                مراجعة الكاميرات
              </div>
            </div>
          </a>

          <div className="sv-header-divider" style={{ width: 1, height: 32, background: "#1e3a5f", flexShrink: 0 }} />

          {/* Tabs */}
          <div
            className="sv-header-tabs"
            style={{
              display: "flex",
              gap: 2,
              flex: 1,
              overflowX: "auto",
              scrollbarWidth: "none",
              padding: "2px 0",
            }}
          >
            {TABS.filter((t) => {
              if (user.role === "viewer") return VIEWER_ALLOWED_TABS.includes(t.id);
              // "auditor" sees everything read-only except the import
              // screen, which is a pure write action with nothing to view.
              if (user.role === "auditor") return t.id !== "import";
              return t.id !== "users" || user.role === "admin";
            }).map(
              (t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  style={{
                    background: "transparent",
                    color: tab === t.id ? "#f1f5f9" : "#64748b",
                    border: "none",
                    borderRadius: 8,
                    padding: "7px 10px",
                    cursor: "pointer",
                    fontSize: 11,
                    fontFamily: "inherit",
                    fontWeight: tab === t.id ? 700 : 400,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    position: "relative",
                    transition: "all .2s",
                  }}
                  onMouseEnter={(e) => { if (tab !== t.id) e.currentTarget.style.background = "#ffffff08"; }}
                  onMouseLeave={(e) => { if (tab !== t.id) e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{ fontSize: 14 }}>{t.icon}</span>
                  <span>{t.label}</span>
                  {tab === t.id && (
                    <div style={{
                      position: "absolute", bottom: 0, left: "20%", right: "20%",
                      height: 2.5, borderRadius: "2px 2px 0 0",
                      background: "linear-gradient(90deg,#38bdf8,#818cf8)",
                    }} />
                  )}
                </button>
              )
            )}
          </div>

          {/* Right section */}
          <div className="sv-header-right" style={{
            display: "flex", alignItems: "center", gap: 8, flexShrink: 0,
          }}>
            <MonthPicker value={month} onChange={setMonth} />

            <div style={{ width: 1, height: 24, background: "#1e3a5f" }} />

            {/* User */}
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "#ffffff06", borderRadius: 8,
              padding: "4px 10px 4px 6px",
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: 7,
                background: user.role === "admin"
                  ? "linear-gradient(135deg,#ef4444,#dc2626)"
                  : user.role === "viewer"
                    ? "linear-gradient(135deg,#38bdf8,#0284c7)"
                    : user.role === "auditor"
                      ? "linear-gradient(135deg,#a78bfa,#7c3aed)"
                      : "linear-gradient(135deg,#f59e0b,#d97706)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12,
              }}>
                {user.role === "admin" ? "👑" : user.role === "viewer" ? "📄" : user.role === "auditor" ? "👁️" : "🔧"}
              </div>
              <span style={{ color: "#cbd5e1", fontSize: 12, fontWeight: 600 }}>
                {user.full_name.split(" ").slice(0, 2).join(" ")}
              </span>
            </div>

            <button
              onClick={onLogout}
              title="تسجيل الخروج"
              style={{
                background: "#1e293b", border: "1px solid #334155",
                borderRadius: 8, color: "#94a3b8",
                padding: "6px 10px", cursor: "pointer",
                fontSize: 12, fontFamily: "inherit",
                transition: "all .2s", lineHeight: 1,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#334155"; e.currentTarget.style.color = "#f1f5f9"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#1e293b"; e.currentTarget.style.color = "#94a3b8"; }}
            >
              🚪
            </button>
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 1500, margin: "0 auto", padding: "20px 16px" }}>
        {tab === "dashboard" && <Dashboard {...props} />}
        {tab === "daily" && <DailyEntry {...props} />}
        {tab === "deficit" && <DeficitSection {...props} />}
        {tab === "reports" && <Reports {...props} />}
        {tab === "attendance_report" && <AttendanceReport {...props} />}
        {tab === "reviewers" && <ReviewersMgmt {...props} reviewers={reviewers} />}
        {tab === "stations" && <StationsMgmt {...props} />}
        {tab === "assignments" && <AssignmentsMgmt {...props} />}
        {tab === "salaries" && <Salaries {...props} />}
        {tab === "users" &&
          (user.role === "admin" || user.role === "auditor") && (
            <UsersMgmt {...props} />
          )}
        {tab === "import" && user.role !== "auditor" && <ImportExcel {...props} />}
      </div>
    </div>
  );
}
